import InstagramTaggedProductQuery from "@apps/instagram/queries/InstagramTaggedProductQuery";
import InstagramTaggedProductService from "@apps/instagram/services/InstagramTaggedProductService";
import InstagramTaggedProductStore from "@apps/instagram/stores/InstagramTaggedProductStore";
import TikTokGalleryQuery from "@apps/tiktok/queries/TikTokGalleryQuery";
import { TikTokPostsQuery } from "@apps/tiktok/queries/TikTokPostQuery";
import TikTokGalleryService from "@apps/tiktok/services/TikTokGalleryService";
import { TikTokPostsService } from "@apps/tiktok/services/TikTokPostsService";
import TikTokGalleryStore from "@apps/tiktok/stores/TikTokGalleryStore";
import { TikTokPostsStore } from "@apps/tiktok/stores/TikTokPostsStore";
import { tiktokAuthApiClientToken } from "@apps/tiktok/tokens";
import { CartModule } from "@modules/cart/CartModule";
import { CartQuery } from "@modules/cart/queries/CartQuery";
import { CartService } from "@modules/cart/services/CartService";
import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
import { CommonModule } from "@modules/common_module/CommonModule";
import LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
import { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
import { I18nModule } from "@modules/i18n/I18nModule";
import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import {
    productsQueryToken,
    productsServiceToken,
} from "@modules/products/tokens";
import { assert } from "superstruct";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import TikTokGalleryWidgetComponent from "./TikTokGalleryWidget.svelte";
import {
    TikTokGalleryWidgetConfigInterface,
    TikTokGalleryWidgetConfigSchema,
} from "./TikTokGalleryWidgetConfig";

@injectable()
export default class TikTokGalleryWidget implements WidgetInterface {
    galleryStore: TikTokGalleryStore;
    galleryService: TikTokGalleryService;
    galleryQuery: TikTokGalleryQuery;

    postsStore: TikTokPostsStore;
    postsService: TikTokPostsService;
    postsQuery: TikTokPostsQuery;

    taggedProductStore: InstagramTaggedProductStore;
    taggedProductService: InstagramTaggedProductService;
    taggedProductQuery: InstagramTaggedProductQuery;

    productsQuery: ProductsQuery;
    productsService: ProductsServiceInterface;

    locationController: LocationControllerInterface;
    cartService: CartService;
    cartQuery: CartQuery;
    designMode: boolean;
    urlsBuilder: URLsBuilder;
    cartAvailabilityProvider: CartAvailabilityProvider;

    constructor(
        private readonly config: TikTokGalleryWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("TikTokGalleryWidget.constructor", {
            config,
        });
        this.logger.debug("TikTokGalleryWidget is initiaized", config);
        assert(config, TikTokGalleryWidgetConfigSchema);

        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "tiktok.critical.TikTokGalleryWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "tiktok.critical.TikTokGalleryWidget"
            )
        );

        const api = container.resolve(tiktokAuthApiClientToken);
        this.locationController =
            container.resolve(CommonModule).exports.locationController;
        this.cartService = container.resolve(CartModule).exports.cartService;
        this.cartQuery = container.resolve(CartModule).exports.cartQuery;
        this.designMode =
            container
                .resolve(CommonModule)
                .exports.designModeProvider.isDesignMode() || false;
        this.urlsBuilder = container.resolve(CommonModule).exports.urlsBuilder;
        this.cartAvailabilityProvider =
            container.resolve(CommonModule).exports.cartAvailabilityProvider;

        // galleries store/query/service
        this.galleryStore = new TikTokGalleryStore();
        this.galleryService = new TikTokGalleryService(
            this.galleryStore,
            api,
            this.logger
        );
        this.galleryQuery = new TikTokGalleryQuery(this.galleryStore);

        // posts store/query/service
        this.postsStore = new TikTokPostsStore();
        this.postsService = new TikTokPostsService(
            this.postsStore,
            api,
            this.logger
        );
        this.postsQuery = new TikTokPostsQuery(this.postsStore);

        // taggedProduct store/query/service
        this.taggedProductStore = new InstagramTaggedProductStore();
        this.taggedProductService = new InstagramTaggedProductService(
            this.taggedProductStore,
            api,
            this.logger
        );
        this.taggedProductQuery = new InstagramTaggedProductQuery(
            this.taggedProductStore
        );

        this.productsService = container.resolve(productsServiceToken);
        this.productsQuery = container.resolve(productsQueryToken);
    }
    init(): this {
        const element =
            typeof this.config.target === "string"
                ? document.querySelector(this.config.target)
                : this.config.target;
        new TikTokGalleryWidgetComponent({
            target: element as HTMLElement,
            props: {
                galleryId: this.config.galleryId,
                cartService: this.cartService,
                cartQuery: this.cartQuery,
                designMode: this.designMode,
                urlsBuilder: this.urlsBuilder,

                locationController: this.locationController,

                tikTokGalleryQuery: this.galleryQuery,
                tikTokGalleryService: this.galleryService,

                tiktokPostsQuery: this.postsQuery,
                tiktokPostsService: this.postsService,
                tikTokPostsStore: this.postsStore,

                taggedProductQuery: this.taggedProductQuery,
                taggedProductService: this.taggedProductService,

                productsQuery: this.productsQuery,
                productsService: this.productsService,

                cartAvailabilityProvider: this.cartAvailabilityProvider,
            },
        });
        return this;
    }
}
